<template>
  <div class="car-detail-container">
    <div class="car-detail" v-if="carDetail">
      <ClientOnly>
        <PageComponentsCarDetailPrintView />
      </ClientOnly>

      <div class="print:hidden">
        <PageComponentsCarDetailMobileMainDetails class="block md:hidden" />
        <PageComponentsCarDetailDesktopMainDetails class="hidden md:block" />
        <div class="w-full grid grid-cols-1 gap-10 md:grid-cols-[1fr_400px] md:gap-5 mt-5 lg:grid-cols-[1fr_400px] lg:gap-10 xl:grid-cols-[1fr_400px] xl:gap-20 lg:mt-10">
          <div class="w-full order-2 md:order-1">
            <PageComponentsCarDetailTextContents />
          </div>
          <div class="w-full flex flex-col gap-10 md:gap-6 order-1 md:order-2">
            <PageComponentsCarDetailActionButtons />
            <PageComponentsCarDetailCarNumericals />
          </div>
        </div>
        <PageComponentsCarDetailContactForm v-if="component?.carDetailShowContactForm" :component="component" :index="index" />
        <div class="w-full py-[24px] md:py-[40px] lg:py-[60px]" v-if="carModel?.infobox">
          <PageComponentsCarDetailModelInfobox :infobox="carModel?.infobox" />
        </div>
      </div>

      <PageComponentsCarDetailFloatingActions />
    </div>
    <div v-else role="status" class="flex justify-center items-center h-[50dvh]">
      <svg aria-hidden="true" class="w-20 h-20 text-gray-200 animate-spin dark:text-primary fill-accent" viewBox="0 0 100 101" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path
          d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
          fill="currentColor"
        />
        <path
          d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
          fill="currentFill"
        />
      </svg>
      <span class="sr-only">Loading...</span>
    </div>
    <SharedBookTestDrive />
  </div>
</template>

<script setup lang="ts">
import type { GraphQLCarDetail, GraphQLModel } from '~/types/graphql';
import type { AuwsObject } from '~/types/types';

const carDetail = inject<Ref<GraphQLCarDetail>>('carDetail');
const carModel = inject<Ref<GraphQLModel>>('carModel');

  useHead({
    script: [
      {
        key: 'autoUncle',
        innerHTML: `
          window.AuEmbed=window.AuEmbed || {
          pushedUrls:[],pushUrl:function(url){AuEmbed.pushedUrls.push(url)}};

          window.AuEmbed.config = {
            id: "hTATJmI9hE", 
          };

          document.head.querySelector("meta[name=viewport]")||console.warn('The AutoUncle Universal Script cannot detect any  tag. Make sure that the script is added after this tag!')
          var d={depId:AuEmbed.config.departmentId,
          device:window.matchMedia("only screen and (max-width: 760px)").matches?"mobile":"desktop",
          pageType:AuEmbed.config.pageType,
          url:AuEmbed.config.previewUrl||document.location.href
          },s=document.createElement("script")
          s.async=!0,s.src="https://www.autouncle.at/de-at/customers/"+encodeURIComponent(AuEmbed.config.id)+"/website_script.js?d="+encodeURIComponent(JSON.stringify(d)),
          s.id="au-cweb-script",
          h=document.getElementsByTagName("head")[0],h.insertBefore(s,h.lastChild); 
          `,
      },
    ],
  });

  defineProps<{
    component: GraphQLCarDetail;
    index: number;
  }>();

declare global {
  interface Window {
    // AutoUncle widget namespace injected by external script
    auws: AuwsObject | undefined;
  }
}

  const getCurrentDepartmentId = (carDetail: GraphQLCarDetail) => {
    const location = carDetail?.location;
    if (!location) return;

    if (location.toLowerCase().trim().includes('oberwart')) return 'kMnyqXmuYw'; // Oberwart
    if (location.toLowerCase().trim().includes('hofstätten')) return 'gvn31pUScd'; // Gleisdorf-Süd
    if (location.toLowerCase().trim().includes('gleisdorf')) return '64HM9v8vJe'; // Gleisdorf

    return;
  };

  // The AutoUncle widget may attach to `window.auws` before it's fully ready —
  // most notably `ModuleTypes` and `openModal` populate after the rest of the
  // object exists. If a user clicks during that window we'd previously crash
  // dereferencing `auws.ModuleTypes.TRADE_IN`.
  const handleOpenTradeInForm = (carDetail: { carDetail: GraphQLCarDetail }) => {
    if (typeof window.auws?.openModal !== 'function') return;
    if (!carDetail?.carDetail) return;
    const carData = carDetail.carDetail;

    const departmentId = getCurrentDepartmentId(carData) || '';
    const idnr = carData.internal_vehicle_no?.toString() || '';
    const moduleType = window.auws.ModuleTypes?.TRADE_IN ?? 'trade_in';

    window.auws.openModal(moduleType, {
      departmentId: departmentId,
      vehicleId: idnr,
      stockId: idnr,
    });
  };

  const handleOpenPriceWatcherModal = (carDetail: { carDetail: GraphQLCarDetail }) => {
    if (typeof window.auws?.openModal !== 'function') return;
    if (!carDetail?.carDetail) return;
    const carData = carDetail.carDetail;

    const departmentId = getCurrentDepartmentId(carData) || '';
    const idnr = carData.internal_vehicle_no?.toString() || '';
    const moduleType = window.auws.ModuleTypes?.PRICE_ALERT ?? 'price_alert';

    window.auws.openModal(moduleType, {
      departmentId: departmentId,
      vehicleId: idnr,
      stockId: idnr,
    });
  };

  const eventBus = useEventBus();
  eventBus.on('open-trade-in-form', handleOpenTradeInForm);
  eventBus.on('open-price-watcher-modal', handleOpenPriceWatcherModal);

  watch(
    () => carDetail,
    () => {
      if (window.auws) {
        window.auws.reload();
      }
    }
  );
</script>

<style lang="scss" scoped></style>
